import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  User,
  AdminPermissions,
  PayrollActivityAction,
  PayrollActivityEntry,
  PayrollRecord,
  SavePayrollRequest,
  UserRole,
  UserStatus,
  CreateUserRequest,
  Application,
  ApplicationStatusHistory,
  ApplicationFilters,
  ApplicationReportRow,
  ApplicationReportFilters,
  PaginatedResponse,
  ScholarshipProgram,
  UploadedDocument,
  RequiredDocument,
  DocumentRequirement,
  DocumentVerificationStatus,
  Scholar,
  ScholarFilters,
  Grade,
  Renewal,
  RenewalFilters,
  Allowance,
  Violation,
  Announcement,
  AnnouncementFilters,
  CreateAnnouncementRequest,
  Notification,
  QueueTicketStatus,
  Applicant,
  UpdateApplicantProfileRequest,
  ProfileCompleteness,
  CreateScholarshipProgramRequest,
  UpdateScholarshipProgramRequest,
  DashboardStats,
  DeletionRequest,
  DeleteResult,
  HomeContent,
  AboutContent,
  LegalDocumentContent,
  LegalDocumentKey,
  DuplicateApplicantMatch,
  DuplicateUserFlag
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

// Shown instead of a raw "Failed to fetch" or an error page whenever the
// server is too busy to answer. Kept in step with the server's own wording
// in lib/passwordHashing.ts.
export const SERVER_BUSY_MESSAGE = 'Traffic is very busy right now. Try again later.';

export const MAINTENANCE_MESSAGE = 'The system is temporarily closed. Please try again later.';

function busyError(): Error & { code: string } {
  return Object.assign(new Error(SERVER_BUSY_MESSAGE), { code: 'SERVER_BUSY' });
}
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
// How often a queued upload re-checks its position. Kept short so an
// upload starts promptly once a slot frees rather than waiting out a long
// tick — the poll is cheap (no file, just a position lookup), so a faster
// cadence costs little. (The login queue still polls at its own slower
// rate; sign-in isn't where the wait hurts.)
const UPLOAD_QUEUE_POLL_MS = 1000;

/**
 * API Service
 * Centralized service for all API requests
 */
class ApiService {
  private token: string | null = localStorage.getItem('token');

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    endpoint: string,
    body?: any
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      method,
      headers,
      // Belt-and-suspenders alongside the server's own Cache-Control:
      // no-store — every endpoint here is dynamic/authenticated data, so a
      // browser-cached GET response could otherwise show stale data after
      // something changes elsewhere (e.g. a deleted application still
      // appearing on the Dashboard or Reports page).
      cache: 'no-store'
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    try {
      let response: Response;
      try {
        response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      } catch {
        // No usable answer at all. When the server is swamped, the
        // platform's gateway answers instead of the app, without the
        // headers the browser needs, so all it can report is "Failed to
        // fetch" — which tells nobody anything.
        throw busyError();
      }

      // Overloaded, restarting, or timed out (the platform's own error
      // pages, which are HTML, not this API's JSON) — unless it's the
      // system deliberately closed for maintenance, which says so itself.
      if (response.status === 502 || response.status === 503 || response.status === 504) {
        const body = await response.json().catch(() => null);
        if (body?.code === 'MAINTENANCE') {
          throw Object.assign(new Error(body.error || MAINTENANCE_MESSAGE), { code: 'MAINTENANCE' });
        }
        throw busyError();
      }

      if (response.status === 401) {
        // Token expired, clear and redirect to login. A hard navigation
        // here (this file has no access to React Router's basename) must
        // still respect Vite's configured base path — a bare '/login'
        // would 404 on GitHub Pages' /CEAP/ subpath.
        this.clearToken();
        window.location.href = `${import.meta.env.BASE_URL}login`;
      }

      if (!response.ok) {
        // Tolerates a non-JSON body, so an unexpected error page reads as
        // a failed request rather than a confusing parse error.
        const error = await response.json().catch(() => ({}));

        // The account was disabled while this session was open. The
        // 401 branch above does not cover it (the token is still
        // valid, the account is not), and without this the user is
        // left on a page where every request fails and nothing
        // offers them a way out.
        if (response.status === 403 && error.code === 'ACCOUNT_DISABLED') {
          this.clearToken();
          window.location.href = `${import.meta.env.BASE_URL}login`;
        }

        const failure = new Error(error.error || error.message || 'API request failed');
        // Some failures need telling apart, not just displaying — a
        // login refused for an unconfirmed address should offer to
        // resend the link rather than blame the password. The server
        // sends a `code` for those; it would be lost if only the
        // message survived.
        if (error.code) {
          (failure as Error & { code?: string }).code = error.code;
        }
        throw failure;
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error (${method} ${endpoint}):`, error);
      throw error;
    }
  }

  // ============== AUTHENTICATION ==============

  // Requires a ticket that has already earned its turn in the login
  // queue (see joinLoginQueue/checkLoginQueue below) — the server
  // rejects a login attempt with an unready/unknown ticket.
  async login(email: string, password: string, ticket: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('POST', '/auth/login', {
      email,
      password,
      ticket
    });

    if (response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  // "Take a ticket" — called before the login form is shown. `email` is
  // optional and only used server-side to check the Admin/Super Admin
  // exemption — it is not verified here, so passing it isn't a login
  // attempt. See QueueTicketStatus for the response shape.
  async joinLoginQueue(email?: string): Promise<QueueTicketStatus> {
    return this.request<QueueTicketStatus>('POST', '/auth/queue/join', { email });
  }

  // Polled while a ticket is still waiting (not yet ready).
  async checkLoginQueue(ticket: string): Promise<QueueTicketStatus> {
    return this.request<QueueTicketStatus>('GET', `/auth/queue/status?ticket=${encodeURIComponent(ticket)}`);
  }

  // Whether the Super Admin has the queue switched on. Public — the
  // sign-in page reads it while signed out to decide whether to show the
  // ticket step. The server re-checks on login regardless, so this only
  // ever affects what's displayed.
  async getQueuingEnabled(): Promise<boolean> {
    const result = await this.request<{ enabled: boolean }>('GET', '/settings/queuing');
    return result.enabled;
  }

  // Super Admin only.
  // Public — whether the system is closed to students for maintenance.
  async getMaintenanceMode(): Promise<boolean> {
    const result = await this.request<{ enabled: boolean }>('GET', '/settings/maintenance');
    return result.enabled;
  }

  // Super Admin only.
  async setMaintenanceMode(enabled: boolean): Promise<boolean> {
    const result = await this.request<{ enabled: boolean }>('PUT', '/settings/maintenance', { enabled });
    return result.enabled;
  }

  // Public — whether "Forgot password?" emails a reset link.
  async getPasswordResetEmailEnabled(): Promise<boolean> {
    const result = await this.request<{ enabled: boolean }>('GET', '/settings/password-reset-email');
    return result.enabled;
  }

  // Super Admin only.
  async setPasswordResetEmailEnabled(enabled: boolean): Promise<boolean> {
    const result = await this.request<{ enabled: boolean }>('PUT', '/settings/password-reset-email', { enabled });
    return result.enabled;
  }

  async setQueuingEnabled(enabled: boolean): Promise<boolean> {
    const result = await this.request<{ enabled: boolean }>('PUT', '/settings/queuing', { enabled });
    return result.enabled;
  }

  // Super Admin only. unverifiedCount is how many accounts would be
  // refused if this were switched on.
  async getEmailVerificationSetting(): Promise<{ enabled: boolean; unverifiedCount: number }> {
    return this.request('GET', '/settings/email-verification');
  }

  async setEmailVerificationRequired(enabled: boolean): Promise<boolean> {
    const result = await this.request<{ enabled: boolean }>('PUT', '/settings/email-verification', { enabled });
    return result.enabled;
  }

  // Super Admin only. Marks every existing unverified account verified.
  async grandfatherExistingUsers(): Promise<number> {
    const result = await this.request<{ verified: number }>('POST', '/settings/email-verification/grandfather');
    return result.verified;
  }

  // Public — offered on the sign-in page when a login is refused for an
  // unconfirmed address.
  async resendVerificationEmail(email: string): Promise<void> {
    await this.request('POST', '/auth/resend-verification', { email });
  }

  // Requires a ready ticket from the same queue login uses — registration
  // hashes a password, which is the same CPU cost. The ticket stays valid
  // afterwards so the auto-login that follows reuses it (and releases it).
  // The two consent versions are the exact wording of each document the
  // applicant was shown — one tick box agrees to both. Required by the
  // server, which checks each against what is published: the tick box
  // alone is a client-side control and this endpoint is public.
  async register(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    ticket: string,
    privacyConsentVersion: string,
    termsConsentVersion: string
  ): Promise<User> {
    return this.request<User>('POST', '/auth/register', {
      firstName,
      lastName,
      email,
      password,
      ticket,
      privacyConsentVersion,
      termsConsentVersion
    });
  }

  async verifyEmail(token: string): Promise<{ message: string; user: User }> {
    return this.request('POST', '/auth/verify-email', { token });
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('GET', '/auth/me');
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('POST', '/auth/forgot-password', {
      email
    });
  }

  async confirmPasswordReset(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('POST', '/auth/reset-password', {
      token,
      newPassword
    });
  }

  // The change ends every session for the account, including this one,
  // so the server hands back a fresh token and it's swapped in here —
  // otherwise the next request from this tab would be signed out.
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string; user: User }> {
    const result = await this.request<{ message: string; token: string; user: User }>('POST', '/auth/change-password', {
      currentPassword,
      newPassword
    });
    if (result.token) this.setToken(result.token);
    return { message: result.message, user: result.user };
  }

  // ---- Payroll ----

  // Reports one change on the Payroll page to the Super Admin's feed.
  async logPayrollActivity(action: PayrollActivityAction, details?: Record<string, unknown>): Promise<void> {
    await this.request('POST', '/payroll/activity', { action, details });
  }

  // Super Admin only. afterId returns only entries newer than it.
  async getPayrollActivity(afterId?: number): Promise<PayrollActivityEntry[]> {
    return this.request('GET', `/payroll/activity${afterId ? `?afterId=${afterId}` : ''}`);
  }

  // Saves a copy of the payroll about to be printed or downloaded.
  async savePayrollRecord(payload: SavePayrollRequest): Promise<PayrollRecord> {
    return this.request('POST', '/payroll/records', payload);
  }

  // Super Admin only.
  async getPayrollRecords(page = 1, pageSize = 20): Promise<PaginatedResponse<PayrollRecord>> {
    return this.request('GET', `/payroll/records?page=${page}&pageSize=${pageSize}`);
  }

  // ============== APPLICANT PROFILE ==============

  async getMyProfile(): Promise<Applicant> {
    return this.request('GET', '/applicants/me');
  }

  async updateMyProfile(data: UpdateApplicantProfileRequest): Promise<Applicant> {
    return this.request('PUT', '/applicants/me', data);
  }

  // `scholarshipId` folds that programme's own required documents into
  // the answer. Omit it for the baseline the Profile page shows, which
  // belongs to no scholarship in particular.
  async getProfileCompleteness(scholarshipId?: number): Promise<ProfileCompleteness> {
    const suffix = scholarshipId ? `?scholarshipId=${scholarshipId}` : '';
    return this.request('GET', `/applicants/me/completeness${suffix}`);
  }

  async getApplicantProfile(applicantId: number): Promise<Applicant> {
    return this.request('GET', `/applicants/${applicantId}`);
  }

  /**
   * Advisory only - other applicants sharing an ID number, phone number,
   * or name and birthdate with this one. Returns an empty list when the
   * profile has none of those filled in.
   */
  async getApplicantDuplicates(applicantId: number): Promise<DuplicateApplicantMatch[]> {
    return this.request('GET', `/applicants/${applicantId}/duplicates`);
  }

  async getApplicantProfileByUserId(userId: number): Promise<Applicant> {
    return this.request('GET', `/applicants/by-user/${userId}`);
  }

  async setProfileLock(userId: number, locked: boolean): Promise<Applicant> {
    return this.request('PUT', `/applicants/by-user/${userId}/profile-lock`, { locked });
  }

  // ============== SCHOLARSHIPS ==============

  async getScholarships(page = 1, pageSize = 10): Promise<PaginatedResponse<ScholarshipProgram>> {
    return this.request('GET', `/scholarships?page=${page}&pageSize=${pageSize}`);
  }

  async getScholarship(id: number): Promise<ScholarshipProgram> {
    return this.request('GET', `/scholarships/${id}`);
  }

  async getRequiredDocuments(scholarshipId: number): Promise<RequiredDocument[]> {
    return this.request('GET', `/scholarships/${scholarshipId}/required-documents`);
  }

  // ============== DOCUMENT REQUIREMENTS (global, profile-level) ==============

  async getDocumentRequirements(): Promise<DocumentRequirement[]> {
    return this.request('GET', '/document-requirements');
  }

  async createDocumentRequirement(documentType: string, isRequired = true): Promise<DocumentRequirement> {
    return this.request('POST', '/document-requirements', { documentType, isRequired });
  }

  // Switches an existing requirement between required and optional.
  async setDocumentRequirementRequired(id: number, isRequired: boolean): Promise<DocumentRequirement> {
    return this.request('PATCH', `/document-requirements/${id}`, { isRequired });
  }

  async deleteDocumentRequirement(id: number): Promise<{ message: string }> {
    return this.request('DELETE', `/document-requirements/${id}`);
  }

  async createScholarship(data: CreateScholarshipProgramRequest): Promise<ScholarshipProgram> {
    return this.request('POST', '/scholarships', data);
  }

  async updateScholarship(id: number, data: UpdateScholarshipProgramRequest): Promise<ScholarshipProgram> {
    return this.request('PUT', `/scholarships/${id}`, data);
  }

  async deleteScholarship(id: number) {
    return this.request('DELETE', `/scholarships/${id}`);
  }

  // ============== APPLICATIONS ==============

  async getApplications(filters?: Partial<ApplicationFilters>): Promise<PaginatedResponse<Application>> {
    const query = new URLSearchParams(filters as Record<string, string> | undefined).toString();
    return this.request('GET', `/applications?${query}`);
  }

  async getApplication(id: number): Promise<Application> {
    return this.request('GET', `/applications/${id}`);
  }

  async getApplicationReport(filters?: Partial<ApplicationReportFilters>): Promise<PaginatedResponse<ApplicationReportRow>> {
    const params = new URLSearchParams();
    if (filters?.name) params.set('name', filters.name);
    if (filters?.barangay) params.set('barangay', filters.barangay);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.scholarshipId) params.set('scholarshipId', String(filters.scholarshipId));
    if (filters?.yearLevelGroup) params.set('yearLevelGroup', filters.yearLevelGroup);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
    return this.request('GET', `/applications/report?${params.toString()}`);
  }

  async getApplicationHistory(id: number): Promise<ApplicationStatusHistory[]> {
    return this.request('GET', `/applications/${id}/history`);
  }

  async createApplication(scholarshipId: number): Promise<Application> {
    return this.request('POST', '/applications', {
      scholarshipId
    });
  }

  async submitApplication(applicationId: number): Promise<Application> {
    return this.request('POST', `/applications/${applicationId}/submit`, {});
  }

  async updateApplication(id: number, data: { status?: string; comments?: string; receivedDate?: string }): Promise<Application> {
    return this.request('PUT', `/applications/${id}`, data);
  }

  async deleteApplication(id: number): Promise<DeleteResult> {
    return this.request('DELETE', `/applications/${id}`);
  }

  async deleteAllApplications(ids: number[]): Promise<{ deleted: number; skipped: number }> {
    return this.request('DELETE', '/applications', { ids });
  }

  // ============== DOCUMENTS ==============

  async getApplicationDocuments(applicationId: number): Promise<UploadedDocument[]> {
    return this.request('GET', `/applications/${applicationId}/documents`);
  }

  async getMyProfileDocuments(): Promise<UploadedDocument[]> {
    return this.request('GET', '/documents/me');
  }

  // "Take a ticket" for the upload queue, and the poll for it. Both are
  // cheap (no file involved) — see waitForUploadSlot below.
  async joinUploadQueue(): Promise<QueueTicketStatus> {
    return this.request<QueueTicketStatus>('POST', '/documents/upload/queue/join');
  }

  async checkUploadQueue(ticket: string): Promise<QueueTicketStatus> {
    return this.request<QueueTicketStatus>('GET', `/documents/upload/queue/status?ticket=${encodeURIComponent(ticket)}`);
  }

  /**
   * Takes an upload ticket and waits until it's this caller's turn,
   * reporting queue position along the way. The file deliberately stays
   * on the client for the whole wait — the server only ever holds a file
   * in memory once a slot is actually free (see middleware/uploadQueue.ts).
   */
  private async waitForUploadSlot(onQueue?: (status: QueueTicketStatus) => void): Promise<string> {
    let status = await this.joinUploadQueue();

    while (!status.ready) {
      onQueue?.(status);
      await new Promise((resolve) => setTimeout(resolve, UPLOAD_QUEUE_POLL_MS));
      try {
        status = await this.checkUploadQueue(status.ticket);
      } catch {
        // Ticket expired (e.g. the tab was backgrounded long enough for
        // the server to assume it was abandoned) — rejoin at the back of
        // the line rather than failing the upload outright.
        status = await this.joinUploadQueue();
      }
    }

    return status.ticket;
  }

  async uploadDocument(
    documentType: string,
    file: File,
    applicationId?: number,
    onQueue?: (status: QueueTicketStatus) => void
  ): Promise<UploadedDocument> {
    // Fails fast client-side instead of waiting on a full upload round
    // trip just to hit the same 5MB limit the server enforces (upload.ts).
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error('File is too large — the maximum upload size is 5MB.');
    }

    const ticket = await this.waitForUploadSlot(onQueue);

    const formData = new FormData();
    if (applicationId !== undefined) {
      formData.append('applicationId', applicationId.toString());
    }
    formData.append('documentType', documentType);
    formData.append('file', file);

    // Ticket rides as a header, not a form field, so the server can check
    // it before parsing (and buffering) the body.
    const headers: HeadersInit = { 'X-Upload-Ticket': ticket };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || 'Failed to upload document');
    }

    return response.json();
  }

  async deleteDocument(documentId: number): Promise<{ message: string }> {
    return this.request('DELETE', `/documents/${documentId}`);
  }

  async downloadDocument(documentId: number): Promise<{ blob: Blob; fileName: string }> {
    const headers: HeadersInit = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/documents/${documentId}/download`, { headers, cache: 'no-store' });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || 'Failed to download document');
    }

    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const fileName = match ? decodeURIComponent(match[1]) : `document-${documentId}`;

    return { blob: await response.blob(), fileName };
  }

  // Super Admin only — a saved payroll copy (PDF or .xlsx).
  async downloadPayrollRecord(recordId: number): Promise<Blob> {
    const headers: HeadersInit = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    const response = await fetch(`${API_BASE_URL}/payroll/records/${recordId}/download`, { headers, cache: 'no-store' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || 'Failed to download the payroll copy');
    }
    return response.blob();
  }

  async downloadApplicationFormPdf(): Promise<Blob> {
    const headers: HeadersInit = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    // cache: 'no-store' plus a cache-busting query param, on top of the
    // server's own Cache-Control: no-store — belt-and-suspenders against
    // any browser, proxy, or CDN layer that might otherwise serve a
    // stale copy from before the applicant's most recent profile edit.
    const response = await fetch(`${API_BASE_URL}/applicants/me/application-form.pdf?t=${Date.now()}`, {
      headers,
      cache: 'no-store'
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || 'Failed to generate application form');
    }

    return response.blob();
  }

  async verifyDocument(
    documentId: number,
    status: DocumentVerificationStatus,
    notes?: string
  ): Promise<UploadedDocument> {
    return this.request('PUT', `/documents/${documentId}/verify`, { status, notes });
  }

  // ============== SCHOLARS ==============

  // A person can hold a Scholar record in more than one program at
  // once, so this returns every one they have — empty array, not null,
  // when they aren't a Scholar anywhere.
  async getMyScholarRecords(): Promise<Scholar[]> {
    try {
      return await this.request<Scholar[]>('GET', '/scholars/me');
    } catch {
      return [];
    }
  }

  async getScholars(filters?: Partial<ScholarFilters>): Promise<PaginatedResponse<Scholar>> {
    const query = new URLSearchParams(filters as Record<string, string> | undefined).toString();
    return this.request('GET', `/scholars?${query}`);
  }

  async getScholar(id: number): Promise<Scholar> {
    return this.request('GET', `/scholars/${id}`);
  }

  async deleteScholar(id: number): Promise<DeleteResult> {
    return this.request('DELETE', `/scholars/${id}`);
  }

  async deleteAllScholars(ids: number[]): Promise<{ deleted: number; skipped: number }> {
    return this.request('DELETE', '/scholars', { ids });
  }

  async getGrades(scholarId: number): Promise<Grade[]> {
    return this.request('GET', `/scholars/${scholarId}/grades`);
  }

  async submitGrade(
    scholarId: number,
    data: { academicYear: string; semester: string; gpa: number }
  ): Promise<Grade> {
    return this.request('POST', `/scholars/${scholarId}/grades`, data);
  }

  async getRenewals(scholarId: number): Promise<Renewal[]> {
    return this.request('GET', `/scholars/${scholarId}/renewals`);
  }

  async requestRenewal(scholarId: number, data: { academicYear: string; semester: string }): Promise<Renewal> {
    return this.request('POST', `/scholars/${scholarId}/renewals`, data);
  }

  async reviewRenewal(renewalId: number, decision: 'approved' | 'rejected', notes?: string): Promise<Renewal> {
    return this.request('PUT', `/renewals/${renewalId}`, { decision, notes });
  }

  async getAllRenewals(filters?: Partial<RenewalFilters>): Promise<PaginatedResponse<Renewal>> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.scholarshipId) params.set('scholarshipId', String(filters.scholarshipId));
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
    return this.request('GET', `/renewals?${params.toString()}`);
  }

  async markRenewalUnderReview(renewalId: number): Promise<Renewal> {
    return this.request('PUT', `/renewals/${renewalId}/under-review`, {});
  }

  async getAllowances(scholarId: number): Promise<Allowance[]> {
    return this.request('GET', `/scholars/${scholarId}/allowances`);
  }

  async createAllowance(
    scholarId: number,
    data: { academicYear: string; semester: string; amount: number; paymentDate?: string }
  ): Promise<Allowance> {
    return this.request('POST', `/scholars/${scholarId}/allowances`, data);
  }

  async releaseAllowance(allowanceId: number): Promise<Allowance> {
    return this.request('POST', `/allowances/${allowanceId}/release`, {});
  }

  async getViolations(scholarId: number): Promise<Violation[]> {
    return this.request('GET', `/scholars/${scholarId}/violations`);
  }

  async createViolation(
    scholarId: number,
    data: { violationType: string; description: string; severity: string; actionTaken: string }
  ): Promise<Violation> {
    return this.request('POST', `/scholars/${scholarId}/violations`, data);
  }

  // ============== DASHBOARD ==============

  async getDashboardStats(): Promise<DashboardStats> {
    return this.request('GET', '/dashboard/stats');
  }

  // ============== PUSH NOTIFICATIONS ==============

  // `enabled` is false when the server has no VAPID keys, so the UI can
  // hide the opt-in rather than offer a button that cannot work.
  async getPushPublicKey(): Promise<{ enabled: boolean; publicKey: string | null }> {
    return this.request('GET', '/push/public-key');
  }

  async subscribeToPush(subscription: PushSubscriptionJSON): Promise<void> {
    await this.request('POST', '/push/subscribe', subscription);
  }

  async unsubscribeFromPush(endpoint: string): Promise<void> {
    await this.request('POST', '/push/unsubscribe', { endpoint });
  }

  // Sends to the caller's own devices, so turning this on can be
  // confirmed rather than assumed.
  async sendTestPush(): Promise<void> {
    await this.request('POST', '/push/test');
  }

  // ============== SITE CONTENT ==============

  // Public — the landing page calls this while signed out.
  async getHomeContent(): Promise<HomeContent> {
    return this.request('GET', '/site-content/home');
  }

  // Super Admin only. Sends the whole page at once; the server replies
  // with what it actually stored, so the editor re-renders from that
  // rather than from its own optimistic copy.
  async updateHomeContent(content: HomeContent): Promise<HomeContent> {
    return this.request('PUT', '/site-content/home', content);
  }

  // Public — read while signed out by the two legal pages, and by the
  // consent box on the registration form.
  async getLegalDocument(doc: LegalDocumentKey): Promise<LegalDocumentContent> {
    return this.request('GET', `/site-content/legal/${doc}`);
  }

  // The shipped text, for the editor's "restore the original" action.
  // Fetched rather than bundled so the prose has one home (the server).
  async getLegalDocumentDefault(doc: LegalDocumentKey): Promise<LegalDocumentContent> {
    return this.request('GET', `/site-content/legal/${doc}?defaults=1`);
  }

  // Super Admin only.
  async updateLegalDocument(doc: LegalDocumentKey, content: LegalDocumentContent): Promise<LegalDocumentContent> {
    return this.request('PUT', `/site-content/legal/${doc}`, content);
  }

  // About Us — public to read, Super Admin to write.
  async getAbout(): Promise<AboutContent> {
    return this.request('GET', '/site-content/about');
  }

  async getAboutDefault(): Promise<AboutContent> {
    return this.request('GET', '/site-content/about?defaults=1');
  }

  async updateAbout(content: AboutContent): Promise<AboutContent> {
    return this.request('PUT', '/site-content/about', content);
  }

  // ============== USERS ==============

  async getUsers(): Promise<User[]> {
    return this.request('GET', '/users');
  }

  // Accounts sharing an ID number, phone number, or name and date of
  // birth with another account. Fetched separately from the user list
  // rather than folded into it: it scans every profile, and a failure
  // to compute it must not stop User Management loading.
  async getDuplicateUsers(): Promise<DuplicateUserFlag[]> {
    return this.request('GET', '/users/duplicates');
  }

  async getUser(id: number): Promise<User> {
    return this.request('GET', `/users/${id}`);
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    return this.request('POST', '/users', data);
  }

  async updateUserAccount(
    id: number,
    updates: { role?: UserRole; status?: UserStatus; isDeletionReviewer?: boolean }
  ): Promise<User> {
    return this.request('PATCH', `/users/${id}`, updates);
  }

  // Super Admin only. A partial set — omitted keys keep their value.
  async updateAdminPermissions(id: number, permissions: Partial<AdminPermissions>): Promise<User> {
    return this.request('PUT', `/users/${id}/permissions`, permissions);
  }

  async resetUserPassword(id: number): Promise<{ user: User; temporaryPassword: string }> {
    return this.request('POST', `/users/${id}/reset-password`);
  }

  async deleteUser(id: number): Promise<DeleteResult> {
    return this.request('DELETE', `/users/${id}`);
  }

  async deleteAllUsers(ids: number[]): Promise<{ deleted: number; skipped: number }> {
    return this.request('DELETE', '/users', { ids });
  }

  async uploadProfilePicture(file: File): Promise<User> {
    // Fails fast client-side instead of waiting on a full upload round
    // trip just to hit the same 5MB limit the server enforces (upload.ts).
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error('File is too large — the maximum upload size is 5MB.');
    }

    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/users/me/profile-picture`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || 'Failed to upload profile picture');
    }

    return response.json();
  }

  async deleteProfilePicture(): Promise<User> {
    return this.request('DELETE', '/users/me/profile-picture');
  }

  async getProfilePictureBlob(userId: number): Promise<Blob> {
    const headers: HeadersInit = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/users/${userId}/profile-picture`, { headers });
    if (!response.ok) {
      throw new Error('No profile picture');
    }
    return response.blob();
  }

  async getDeletionHistory(page = 1, pageSize = 50): Promise<PaginatedResponse<DeletionRequest>> {
    return this.request('GET', `/deletion-requests/history?page=${page}&pageSize=${pageSize}`);
  }

  async getPendingDeletionRequests(page = 1, pageSize = 50): Promise<PaginatedResponse<DeletionRequest>> {
    return this.request('GET', `/deletion-requests?page=${page}&pageSize=${pageSize}`);
  }

  async approveDeletionRequest(id: number): Promise<{ message: string }> {
    return this.request('POST', `/deletion-requests/${id}/approve`);
  }

  async rejectDeletionRequest(id: number, note?: string): Promise<{ message: string }> {
    return this.request('POST', `/deletion-requests/${id}/reject`, note ? { note } : undefined);
  }

  async getUserProfileDocuments(userId: number): Promise<UploadedDocument[]> {
    return this.request('GET', `/users/${userId}/profile-documents`);
  }

  async downloadMergedProfileDocumentsPdf(userId: number): Promise<Blob> {
    const headers: HeadersInit = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/users/${userId}/profile-documents/merged-pdf`, { headers, cache: 'no-store' });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || 'Failed to generate merged PDF');
    }

    return response.blob();
  }

  // ============== ANNOUNCEMENTS ==============

  async getAnnouncements(filters?: Partial<AnnouncementFilters>): Promise<PaginatedResponse<Announcement>> {
    const query = new URLSearchParams(filters as Record<string, string> | undefined).toString();
    return this.request('GET', `/announcements?${query}`);
  }

  async getAnnouncement(id: number): Promise<Announcement> {
    return this.request('GET', `/announcements/${id}`);
  }

  async createAnnouncement(data: CreateAnnouncementRequest): Promise<Announcement> {
    return this.request('POST', '/announcements', data);
  }

  async updateAnnouncement(id: number, data: Partial<CreateAnnouncementRequest>): Promise<Announcement> {
    return this.request('PUT', `/announcements/${id}`, data);
  }

  async deleteAnnouncement(id: number): Promise<{ message: string }> {
    return this.request('DELETE', `/announcements/${id}`);
  }

  // ============== NOTIFICATIONS ==============

  async getNotifications(): Promise<Notification[]> {
    return this.request('GET', '/notifications');
  }

  async getUnreadNotificationCount(): Promise<number> {
    const { count } = await this.request<{ count: number }>('GET', '/notifications/unread-count');
    return count;
  }

  async markNotificationAsRead(id: number): Promise<Notification> {
    return this.request('POST', `/notifications/${id}/read`, {});
  }

  async markAllNotificationsAsRead(): Promise<{ updated: number }> {
    return this.request('POST', '/notifications/read-all', {});
  }

  async deleteNotification(id: number): Promise<{ message: string }> {
    return this.request('DELETE', `/notifications/${id}`);
  }

  async deleteAllNotifications(): Promise<{ deleted: number }> {
    return this.request('DELETE', '/notifications');
  }

  // ============== HEALTH ==============

  async health(): Promise<{ status: string }> {
    return this.request('GET', '/health');
  }
}

export const apiService = new ApiService();
